import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// User roles and their permissions
export const ROLES = {
  ADMIN: 'administrador',
  GERENTE: 'gerente',
  FINANCEIRO: 'financeiro',
  PRODUCAO: 'producao',
};

// Menu permissions by role
export const MENU_PERMISSIONS = {
  [ROLES.ADMIN]: [
    'dashboard', 'orcamento', 'vendas', 'ordem-compra', 'producao', 'clientes', 'fornecedores', 
    'almoxarifado', 'itens-loja', 'equipe', 'freelancer', 'veiculos', 'documentacao', 'financeiro', 'avisos', 'relatorios',
    'novo-usuario', 'configuracoes'
  ],
  [ROLES.GERENTE]: [
    'dashboard', 'orcamento', 'vendas', 'ordem-compra', 'producao', 'clientes', 'fornecedores', 
    'almoxarifado', 'itens-loja', 'equipe', 'freelancer', 'veiculos', 'documentacao', 'financeiro', 'avisos', 'relatorios',
    'configuracoes'
  ],
  [ROLES.FINANCEIRO]: [
    'dashboard', 'vendas', 'ordem-compra', 'clientes', 'fornecedores', 'financeiro', 'avisos', 'relatorios'
  ],
  [ROLES.PRODUCAO]: [
    'dashboard', 'producao', 'almoxarifado', 'itens-loja', 'equipe', 'freelancer', 'veiculos', 'documentacao', 'avisos'
  ],
};

// Dashboard widgets by role
export const DASHBOARD_WIDGETS = {
  [ROLES.ADMIN]: ['avisos', 'ordens', 'grafico', 'producao', 'financeiro'],
  [ROLES.GERENTE]: ['avisos', 'ordens', 'grafico', 'producao', 'financeiro'],
  [ROLES.FINANCEIRO]: ['avisos', 'ordens', 'grafico', 'financeiro'],
  [ROLES.PRODUCAO]: ['avisos', 'producao'],
};

// Role labels
const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Administrador',
  [ROLES.GERENTE]: 'Gerente',
  [ROLES.FINANCEIRO]: 'Financeiro',
  [ROLES.PRODUCAO]: 'Produção',
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from token on mount
  useEffect(() => {
    const loadUser = async () => {
      const token = api.getToken();
      if (token) {
        try {
          const userData = await api.getMe();
          const userWithRole = {
            ...userData,
            role: userData.perfil,
            permissoes: (userData.permissoes && userData.permissoes.length > 0) ? userData.permissoes : (MENU_PERMISSIONS[userData.perfil] || []),
          };
          setUser(userWithRole);
        } catch (error) {
          console.error('Failed to load user:', error);
          api.removeToken();
        }
      }
      setIsLoading(false);
    };

    loadUser();
  }, []);

  // Login function - now uses API
  const login = useCallback(async (email, senha) => {
    try {
      await api.login(email, senha);
      const userData = await api.getMe();
      const userWithRole = {
        ...userData,
        role: userData.perfil,
        permissoes: (userData.permissoes && userData.permissoes.length > 0) ? userData.permissoes : (MENU_PERMISSIONS[userData.perfil] || []),
      };
      setUser(userWithRole);
      return { success: true, user: userWithRole };
    } catch (error) {
      return { success: false, error: error.message || 'E-mail ou senha inválidos' };
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    api.logout();
    setUser(null);
  }, []);

  // Check if user has permission for a menu item
  const hasPermission = useCallback((menuId) => {
    if (!user) return false;
    
    // Use user's specific permissions if available
    if (user.permissoes && Array.isArray(user.permissoes)) {
      return user.permissoes.includes(menuId);
    }
    
    // Fallback to role-based permissions
    const permissions = MENU_PERMISSIONS[user.role] || [];
    return permissions.includes(menuId);
  }, [user]);

  // Check if user can see financial data
  const canSeeFinanceiro = useCallback(() => {
    if (!user) return false;
    return [ROLES.ADMIN, ROLES.GERENTE, ROLES.FINANCEIRO].includes(user.role);
  }, [user]);

  // Check if user can see a dashboard widget
  const canSeeWidget = useCallback((widgetId) => {
    if (!user) return false;
    const widgets = DASHBOARD_WIDGETS[user.role] || [];
    return widgets.includes(widgetId);
  }, [user]);

  // Get role label
  const getRoleLabel = useCallback((role) => {
    return ROLE_LABELS[role] || role;
  }, []);

  // Get users (placeholder - would need an admin API)
  const getUsersByRole = useCallback(() => {
    return {
      administrador: [],
      gerente: [],
      financeiro: [],
      producao: [],
    };
  }, []);

  const getRegisteredUsers = useCallback(() => {
    return [];
  }, []);

  const value = {
    user,
    isLoading,
    login,
    logout,
    hasPermission,
    canSeeFinanceiro,
    canSeeWidget,
    getRoleLabel,
    getUsersByRole,
    getRegisteredUsers,
    ROLES,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
